import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  IconButton,
  Checkbox,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import { useQuery, useMutation, useLazyQuery } from "@apollo/client/react";
import AllClassesDataGrid from "../components/AllClassesDataGrid";
import CustomSnackbar from "../components/Snackbar";
import {
  GET_CLASS_LIST,
  GET_INSTRUCTOR_LIST,
  GET_CLASS_NAMES,
  ME,
  GET_INSTRUCTOR_BY_EMAIL,
} from "../graphql/query";
import { DELETE_CLASS, DELETE_MULTIPLE_CLASSES } from "../graphql/mutation";
import { ClassRow } from "../interfaces/class.interface";
import themestyle from "../theme";
import { formatTo12Hour, calculateDuration } from "../utils/time";
import { AllClassesTableProps } from "../interfaces/allClassesTable.interface";
import { CLASS_TYPE_UI } from "../utils/const";
import { CLASS_TABLE_TEXT } from "../utils/const";
import { useNavigate } from "react-router-dom";
import ConfirmDialog from "../components/confirmdialogbox";

const AllClassesTable: React.FC<AllClassesTableProps> = ({
  onView,
  onEdit,
  onAttendance,
  onDelete,
  refreshTrigger,
  initialTab = "current",
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedInstructor, setSelectedInstructor] = useState<string | null>(
    null,
  );
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [selectedClassType, setSelectedClassType] = useState<string | null>(
    null,
  );
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: isMobile ? 3 : 5,
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState<
    "success" | "error" | "info"
  >("success");

  const [activeTab, setActiveTab] = useState<
    "current" | "upcoming" | "previous"
  >(initialTab);

  const { data: userData } = useQuery(ME, {
    fetchPolicy: "cache-first",
  });

  const storedUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  })();

  const userRole =
    userData?.me?.role?.toUpperCase() || storedUser?.role?.toUpperCase() || "";
  const userEmail = userData?.me?.email || storedUser?.email || "";
  const isInstructor = userRole === "INSTRUCTOR";
  const { data: instructorByEmailData } = useQuery(GET_INSTRUCTOR_BY_EMAIL, {
    variables: { search: userEmail },
    skip: !isInstructor || !userEmail,
    fetchPolicy: "cache-first",
  });
  const loggedInInstructorId: string | null = isInstructor
    ? (instructorByEmailData?.getInstructorList?.data?.[0]?.id ?? null)
    : null;
  const canViewPreviousActions =
    userRole === "ADMIN" || userRole === "SUPER_ADMIN";

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectAllPages, setSelectAllPages] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [activeTab]);
  useEffect(() => {
    if (activeTab !== "upcoming") {
      setSelectedIds([]);
      setSelectAllPages(false);
    }
  }, [activeTab]);

  const handleTabChange = (tab: "current" | "upcoming" | "previous") => {
    setActiveTab(tab);
  };

  const todayDate = new Date();
  const localToday = `${todayDate.getFullYear()}-${String(
    todayDate.getMonth() + 1,
  ).padStart(2, "0")}-${String(todayDate.getDate()).padStart(2, "0")}`;

  const { data, loading, error, previousData, refetch } = useQuery(
    GET_CLASS_LIST,
    {
      variables: {
        page: activeTab === "current" ? paginationModel.page + 1 : 1,
        perPage: activeTab === "current" ? paginationModel.pageSize : 1000,
        search: searchQuery || "",
        instructorName: selectedInstructor,
        instructorId: loggedInInstructorId ?? undefined,
        day: undefined,
        classType: selectedClassType,
        timeline: activeTab.toUpperCase(),
        today: localToday,
        includeCancelled: activeTab === "previous",
      },
      skip: isInstructor && !loggedInInstructorId,
      fetchPolicy: "network-only",
    },
  );

  const {
    data: currentClassesForPreviousData,
    refetch: refetchCurrentClassesForPrevious,
  } = useQuery(GET_CLASS_LIST, {
    variables: {
      page: 1,
      perPage: 1000,
      search: searchQuery || "",
      instructorName: selectedInstructor,
      instructorId: loggedInInstructorId ?? undefined,
      day: undefined,
      classType: selectedClassType,
      timeline: "CURRENT",
      today: localToday,
      includeCancelled: false,
    },
    skip: activeTab !== "previous" || (isInstructor && !loggedInInstructorId),
    fetchPolicy: "network-only",
  });


  const {
    data: allClassesData,
    loading: classOptionsLoading,
    refetch: refetchAllClasses,
  } = useQuery(GET_CLASS_NAMES, {
    variables: {
      page: 1,
      perPage: 1000,
      ...(loggedInInstructorId ? { instructorId: loggedInInstructorId } : {}),
    },
    fetchPolicy: "network-only",
  });

  const { data: instructorsData, refetch: refetchInstructors } = useQuery(
    GET_INSTRUCTOR_LIST,
    {
      variables: { page: 1, perPage: paginationModel.pageSize },
    },
  );


  const [deleteMultipleClasses] = useMutation(DELETE_MULTIPLE_CLASSES);

  const instructorOptions = isInstructor
    ? instructorByEmailData?.getInstructorList?.data || []
    : instructorsData?.getInstructorList?.data || [];

  const classOptions = useMemo(() => {
    if (!allClassesData?.getClassList?.data) return [];
    const uniqueNames = new Map<string, string>();
    allClassesData.getClassList.data.forEach((c: any) => {
      const lower = c.className.toLowerCase();
      if (!uniqueNames.has(lower)) {
        uniqueNames.set(lower, c.className);
      }
    });
    return Array.from(uniqueNames.values());
  }, [allClassesData]);

  useEffect(() => {
    if (error) {
      console.error(error);
    }
  }, [error]);

  useEffect(() => {
    setPaginationModel((prev) => ({
      ...prev,
      pageSize: isMobile ? 3 : 5,
      page: 0,
    }));
  }, [isMobile]);

  useEffect(() => {
    if (refreshTrigger) {
      refetch();
      refetchCurrentClassesForPrevious();
      refetchAllClasses();
      refetchInstructors();
    }
  }, [refreshTrigger]);

  const getDayName = (dateString: string) => {
    if (!dateString || !dateString.includes("-")) return dateString || "";
    const date = new Date(dateString + "T00:00:00");
    if (isNaN(date.getTime())) return dateString;
    return new Intl.DateTimeFormat("en-US", { weekday: "short" }).format(date);
  };

  const formatDateWithDay = (dateString: string) => {
    if (!dateString || !dateString.includes("-")) return dateString || "";
    const date = new Date(dateString + "T00:00:00");
    if (isNaN(date.getTime())) return dateString;
    const d = date.getDate();
    const m = date.getMonth() + 1;
    const y = date.getFullYear();
    const dayName = getDayName(dateString);
    return `${d}.${m}.${y}, ${dayName}`;
  };

  const formatCompletedDate = (dateString: string) => {
    if (!dateString || !dateString.includes("-")) return dateString || "";
    const date = new Date(dateString + "T00:00:00");
    if (isNaN(date.getTime())) return dateString;
    return new Intl.DateTimeFormat("en-US", {
      month: "long",
      day: "2-digit",
      year: "numeric",
    })
      .format(date)
      .replace(",", "");
  };

  const filteredRows = useMemo(() => {
    const list: any[] = activeTab === "previous"
      ? [
        ...(data?.getClassList?.data ?? []),
        ...(currentClassesForPreviousData?.getClassList?.data ?? []),
      ]
      : data?.getClassList?.data ?? [];
    let globalIndex = activeTab === "current" ? paginationModel.page * paginationModel.pageSize : 0;
    const rows = list.flatMap((row) => {
      let dates: string[] =
        Array.isArray(row.scheduledDates) && row.scheduledDates.length > 0
          ? row.scheduledDates
          : row.day
            ? [row.day]
            : [];


      dates = [...dates].sort((a, b) => a.localeCompare(b));
      if (selectedDay) {
        dates = dates.filter((date) => {
          const fullDay = new Intl.DateTimeFormat("en-US", {
            weekday: "long",
          }).format(new Date(date + "T00:00:00"));

          const shortDay = new Intl.DateTimeFormat("en-US", {
            weekday: "short",
          }).format(new Date(date + "T00:00:00"));

          return selectedDay === fullDay || selectedDay === shortDay;
        });
      }


      const parseClassDateTime = (date: string, time: string) => {
        if (!date || !time) return null;

        const normalizedTime = time
          .trim()
          .toUpperCase()
          .replace(/\./g, "")
          .replace(/\s+/g, " ");

        if (/^\d{2}:\d{2}$/.test(normalizedTime)) {
          const parsedDate = new Date(`${date}T${normalizedTime}`);
          return isNaN(parsedDate.getTime()) ? null : parsedDate;
        }

        const match = normalizedTime.match(/^(\d{1,2}):(\d{2})\s?(AM|PM)$/);
        if (!match) return null;

        let hours = Number(match[1]);
        const minutes = Number(match[2]);
        const meridian = match[3];

        if (meridian === "PM" && hours !== 12) hours += 12;
        if (meridian === "AM" && hours === 12) hours = 0;

        const [year, month, day] = date.split("-").map(Number);
        return new Date(year, month - 1, day, hours, minutes);
      };

      const isClassCompletedToday = (date: string, endTime: string) => {
        if (date !== localToday || !endTime) return false;

        const classEndDateTime = parseClassDateTime(date, endTime);
        if (!classEndDateTime) return false;

        return classEndDateTime <= new Date();
      };

      if (activeTab === "current") {
        dates = dates.filter(
          (d) => d === localToday && !isClassCompletedToday(d, row.endTime),
        );
      } else if (activeTab === "upcoming") {
        dates = dates.filter((d) => d > localToday);
      } else if (activeTab === "previous") {
        dates = dates.filter((d) => d < localToday || isClassCompletedToday(d, row.endTime),);
      }

      if (dates.length === 0) return [];

      return dates.map((displayDateStr, di) => {
        globalIndex += 1;
        const serial = globalIndex;
        const isToday = displayDateStr === localToday;
        const timeStr = `${formatTo12Hour(row.startTime)} - ${formatTo12Hour(row.endTime)}`;
        const displayTime = isToday ? `Today, ${timeStr}` : timeStr;
        const dateParts = (displayDateStr || "0-0-0").split("-");
        const [y, m, d] = dateParts.length === 3 ? dateParts : ["0", "0", "0"];
        const formattedMobileDate = displayDateStr ? `${d}/${m}/${y}-${getDayName(displayDateStr)}` : "";
        const durationClean = calculateDuration(row.startTime, row.endTime).replace("h", "hr");
        const mobileTimeStr = `${formatTo12Hour(row.startTime)} - ${formatTo12Hour(row.endTime)} (${durationClean})`;
        const uniqueEnrolled = Array.from(new Set((row.enrolledStudents ?? []) as string[]));
        const enrolledCount = uniqueEnrolled.length;
        const isCancelled = Boolean(row.isCancelled) || (Array.isArray(row.cancelledDates) && row.cancelledDates.includes(displayDateStr));
        const totalNoOfClasses = row.totalNoOfClasses ?? dates.length;

        return {
          id: `${row.id}_${di}`,
          classDocId: row.id,
          sno: String(serial).padStart(2, "0"),
          className: row.className,
          instructor: row.instructorName,
          type: row.classType,
          date: formatDateWithDay(displayDateStr),
          completedDate: formatDateWithDay(displayDateStr),
          time: displayTime,
          schedule: `${formatDateWithDay(displayDateStr)}, ${displayTime}`,
          duration: durationClean,
          mobileDate: formattedMobileDate,
          mobileTime: mobileTimeStr,
          attendance: `${enrolledCount}/30`,
          enrollment: `${enrolledCount}/30`,
          sessionStatus: isCancelled ? "Cancelled" : "Closed",
          isCancelled,
          totalNoOfClasses,
          rawDate: displayDateStr,
          sortDateTime: parseClassDateTime(displayDateStr, row.endTime)?.getTime() ?? 0,
        };
      });
    });
    if (activeTab === "previous") {
      return rows
        .sort((a, b) => b.sortDateTime - a.sortDateTime)
        .map((row, index) => ({
          ...row,
          sno: String(index + 1).padStart(2, "0"),
        }));
    }

    return rows;
  }, [data, currentClassesForPreviousData, paginationModel, activeTab, isMobile, localToday, selectedDay]);

  const handleInstructorChange = (value: string | null) => {
    setSelectedInstructor(value);
    setPaginationModel({ ...paginationModel, page: 0 });
  };

  const handleDayChange = (value: string | null) => {
    setSelectedDay(value);
    setPaginationModel({ ...paginationModel, page: 0 });
  };

  const handleClassTypeChange = (value: string | null) => {
    setSelectedClassType(value);
    setPaginationModel({ ...paginationModel, page: 0 });
  };

  const handleDeleteClick = (id: string, name: string, date?: string) => {
    if (onDelete) onDelete(id, name, date);
  };
  const handleToggleSelectId = useCallback((id: string) => {
    setSelectAllPages(false);
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    const allIds = filteredRows.map((r) => r.id);
    setSelectedIds((prev) => Array.from(new Set([...prev, ...allIds])));
    setSelectAllPages(false);
  }, [activeTab, filteredRows, paginationModel]);

  const handleDeselectAll = useCallback(() => {
    setSelectedIds([]);
    setSelectAllPages(false);
  }, []);

  const handleBulkDeleteClick = useCallback(() => {
    if (selectedIds.length > 0) {
      setBulkDeleteDialogOpen(true);
    }
  }, [selectedIds]);

  const handleBulkDeleteConfirm = async () => {
    try {
      const inputs = selectedIds
        .map((compositeId) => {
          const row = filteredRows.find((r) => r.id === compositeId);
          if (row) {
            return { id: row.classDocId, date: row.rawDate };
          }
          return null;
        })
        .filter(Boolean);
      const { data: response }: any = await deleteMultipleClasses({
        variables: { inputs },
      });
      if (response?.deleteMultipleClasses?.success) {
        const count = response.deleteMultipleClasses.deletedCount;
        setSnackbarMessage(
          `${count} ${count === 1 ? "class" : "classes"} deleted successfully`,
        );
        setSnackbarType("error");
        setSnackbarOpen(true);
        setSelectedIds([]);
        setSelectAllPages(false);
        refetch();
        refetchAllClasses();
        refetchInstructors();
      } else {
        setSnackbarMessage(
          response?.deleteMultipleClasses?.message || "Failed to delete",
        );
        setSnackbarType("error");
        setSnackbarOpen(true);
      }
    } catch (err: any) {
      console.error(err);
    } finally {
      setBulkDeleteDialogOpen(false);
    }
  };

  const totalCount =
    data?.getClassList?.totalCount ??
    previousData?.getClassList?.totalCount ??
    0;
  const selectionCount = selectAllPages ? totalCount : selectedIds.length;

  // ── Column definitions ──────────────────────────────────────────────────────

  const columns: GridColDef[] = useMemo(() => {
    const baseColumns: GridColDef[] = [
      {
        field: "sno",
        headerName: CLASS_TABLE_TEXT.SNO,
        width: 80,
        sortable: false,
      },
      {
        field: "className",
        headerName: CLASS_TABLE_TEXT.CLASS_NAME,
        flex: 1,
        minWidth: 140,
        sortable: false,
      },
      {
        field: "instructor",
        headerName: CLASS_TABLE_TEXT.INSTRUCTOR,
        flex: 1,
        minWidth: 120,
        sortable: false,
      },
    ];

    if (activeTab === "previous" && !isMobile) {
      const previousColumns: GridColDef[] = [
        ...baseColumns,
        {
          field: "type",
          headerName: CLASS_TABLE_TEXT.TYPE,
          flex: 1,
          minWidth: 150,
          align: "center",
          headerAlign: "center",
          sortable: false,
          renderCell: (params) => {
            const cfg = CLASS_TYPE_UI[params.value];
            return (
              <Box
                sx={{
                  px: 2,
                  py: 0.6,
                  borderRadius: "10px",
                  bgcolor: cfg?.bg,
                  color: cfg?.color,
                  fontWeight: 600,
                  fontSize: "13px",
                  textAlign: "center",
                  minWidth: 110,
                }}
              >
                {cfg?.label || "-"}
              </Box>
            );
          },
        },
        {
          field: "duration",
          headerName: CLASS_TABLE_TEXT.DURATION,
          flex: 0.7,
          minWidth: 80,
          sortable: false,
        },
        {
          field: "completedDate",
          headerName: CLASS_TABLE_TEXT.COMPLETED_DATE,
          flex: 1.2,
          minWidth: 160,
          sortable: false,
        },
        {
          field: "time",
          headerName: CLASS_TABLE_TEXT.TIME,
          flex: 1.5,
          minWidth: 180,
          sortable: false,
        },
        {
          field: "attendance",
          headerName: CLASS_TABLE_TEXT.ATTENDANCE,
          flex: 0.8,
          minWidth: 100,
          sortable: false,
        },
        {
          field: "sessionStatus",
          headerName: CLASS_TABLE_TEXT.SESSION_STATUS,
          flex: 1,
          minWidth: 120,
          sortable: false,
          renderCell: (params) => (
            <Typography
              sx={{
                color:
                  params.value === "Cancelled"
                    ? "#B91C1C"
                    : themestyle.colors.error,
                fontWeight: 600,
                fontSize: "13px",
              }}
            >
              {params.value}
            </Typography>
          ),
        },
      ];
      if (canViewPreviousActions) {
        previousColumns.push({
          field: "actions",
          headerName: CLASS_TABLE_TEXT.ACTION,
          flex: 0.5,
          minWidth: 100,
          sortable: false,
          renderCell: (params) => {
            return (
              <Box sx={{ display: "flex", gap: 0.2 }}>
                <IconButton
                  size="small"
                  onClick={() =>
                    onAttendance && onAttendance(params.row.classDocId, params.row.rawDate)
                  }
                >
                  <EventAvailableOutlinedIcon fontSize="small" />
                </IconButton>
                {canViewPreviousActions && (
                  <IconButton
                    size="small"
                    sx={{
                      color: themestyle.colors.activeGreen,
                      "&:hover": { bgcolor: "rgba(155,203,74,0.15)" },
                    }}
                    onClick={() => onView && onView(params.row.classDocId)}
                  >
                    <VisibilityOutlinedIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            );
          },
        });
      }
      return previousColumns;
    }

    if (activeTab === "upcoming" && !isMobile) {
      const upcomingCols: GridColDef[] = [
        ...(userRole !== "INSTRUCTOR"
          ? [
            {
              field: "checkboxSelection",
              headerName: "",
              width: 50,
              sortable: false,
              disableColumnMenu: true,
              renderHeader: () => {
                const visibleRows = filteredRows.slice(
                  paginationModel.page * paginationModel.pageSize,
                  (paginationModel.page + 1) * paginationModel.pageSize,
                );
                const currentPageIds = visibleRows.map((r) => r.id);
                const allChecked =
                  currentPageIds.length > 0 &&
                  currentPageIds.every((id) => selectedIds.includes(id));
                const someChecked =
                  !allChecked &&
                  currentPageIds.some((id) => selectedIds.includes(id));
                return (
                  <Checkbox
                    size="small"
                    checked={allChecked}
                    indeterminate={someChecked}
                    onChange={() => {
                      if (allChecked) {
                        // deselect all on current page
                        setSelectedIds((prev) =>
                          prev.filter((id) => !currentPageIds.includes(id)),
                        );
                        setSelectAllPages(false);
                      } else {
                        // select all on current page
                        setSelectedIds((prev) =>
                          Array.from(new Set([...prev, ...currentPageIds])),
                        );
                      }
                    }}
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      padding: "2px",
                      color: "#D1D5DB",
                      "&.Mui-checked": {
                        color: themestyle.colors.activeGreen,
                      },
                      "&.MuiCheckbox-indeterminate": {
                        color: themestyle.colors.activeGreen,
                      },
                      "& .MuiSvgIcon-root": { fontSize: 16 },
                    }}
                  />
                );
              },
              renderCell: (params: any) => {
                const isChecked = selectedIds.includes(params.row.id);
                return (
                  <Checkbox
                    size="small"
                    checked={isChecked}
                    onChange={() => handleToggleSelectId(params.row.id)}
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      padding: "2px",
                      color: "#D1D5DB",
                      "&.Mui-checked": {
                        color: themestyle.colors.activeGreen,
                      },
                      "& .MuiSvgIcon-root": {
                        fontSize: 16,
                      },
                    }}
                  />
                );
              },
            } as GridColDef,
          ]
          : []),
        ...baseColumns,
        {
          field: "type",
          headerName: CLASS_TABLE_TEXT.TYPE,
          flex: 1,
          minWidth: 150,
          align: "center",
          headerAlign: "center",
          sortable: false,
          renderCell: (params) => {
            const cfg = CLASS_TYPE_UI[params.value];
            return (
              <Box
                sx={{
                  px: 2,
                  py: 0.6,
                  borderRadius: "10px",
                  bgcolor: cfg?.bg,
                  color: cfg?.color,
                  fontWeight: 600,
                  fontSize: "13px",
                  textAlign: "center",
                  minWidth: 110,
                }}
              >
                {cfg?.label || "-"}
              </Box>
            );
          },
        },
        {
          field: "duration",
          headerName: CLASS_TABLE_TEXT.DURATION,
          flex: 0.8,
          minWidth: 100,
          sortable: false,
        },
        {
          field: "date",
          headerName: CLASS_TABLE_TEXT.FUTURE_DATE_DAY,
          flex: 1.2,
          minWidth: 160,
          sortable: false,
        },
        {
          field: "time",
          headerName: CLASS_TABLE_TEXT.TIME,
          flex: 1.5,
          minWidth: 180,
          sortable: false,
        },
        {
          field: "enrollment",
          headerName: CLASS_TABLE_TEXT.ENROLLMENT,
          flex: 0.8,
          minWidth: 100,
          sortable: false,
        },
        {
          field: "actions",
          headerName: CLASS_TABLE_TEXT.ACTION,
          flex: 0.3,
          minWidth: 120,
          sortable: false,
          renderCell: (params) => {
            return (
              <Box sx={{ display: "flex", gap: 0.2 }}>
                <IconButton
                  size="small"
                  onClick={() =>
                    onAttendance && onAttendance(params.row.classDocId, params.row.rawDate)
                  }
                >
                  <EventAvailableOutlinedIcon fontSize="small" />
                </IconButton>
                {userRole !== "INSTRUCTOR" && (
                  <>
                    <IconButton
                      size="small"
                      sx={{
                        color: themestyle.colors.activeGreen,
                        "&:hover": { bgcolor: "rgba(155,203,74,0.15)" },
                      }}
                      onClick={() => onView && onView(params.row.classDocId)}
                    >
                      <VisibilityOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      sx={{
                        color: themestyle.colors.error,
                        "&:hover": { bgcolor: "rgba(229,57,53,0.12)" },
                      }}
                      onClick={() =>
                        handleDeleteClick(
                          params.row.classDocId,
                          params.row.className,
                          params.row.rawDate,
                        )
                      }
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </>
                )}
              </Box>
            );
          },
        },
      ];
      return upcomingCols;
    }

    return [
      ...baseColumns,
      {
        field: "type",
        headerName: CLASS_TABLE_TEXT.TYPE,
        flex: 1,
        minWidth: 150,
        align: "center",
        headerAlign: "center",
        sortable: false,
        renderCell: (params) => {
          const cfg = CLASS_TYPE_UI[params.value];
          return (
            <Box
              sx={{
                px: 2,
                py: 0.6,
                borderRadius: "10px",
                bgcolor: cfg?.bg,
                color: cfg?.color,
                fontWeight: 600,
                fontSize: "13px",
                textAlign: "center",
                minWidth: 110,
              }}
            >
              {cfg?.label || "-"}
            </Box>
          );
        },
      },
      {
        field: "duration",
        headerName: CLASS_TABLE_TEXT.DURATION,
        flex: 0.8,
        minWidth: 100,
        sortable: false,
      },
      {
        field: "date",
        headerName: CLASS_TABLE_TEXT.DAY,
        flex: 1.2,
        minWidth: 160,
        sortable: false,
      },
      {
        field: "time",
        headerName: CLASS_TABLE_TEXT.TIME,
        flex: 1.5,
        minWidth: 180,
        sortable: false,
      },
      {
        field: "actions",
        headerName: CLASS_TABLE_TEXT.ACTION,
        flex: 0.3,
        minWidth: 120,
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ display: "flex", gap: 0.2 }}>
            <IconButton
              size="small"
              onClick={() => onAttendance && onAttendance(params.row.classDocId, params.row.rawDate)}
            >
              <EventAvailableOutlinedIcon fontSize="small" />
            </IconButton>
            {userRole !== "INSTRUCTOR" && (
              <>
                <IconButton
                  size="small"
                  sx={{
                    color: themestyle.colors.activeGreen,
                    "&:hover": { bgcolor: "rgba(155,203,74,0.15)" },
                  }}
                  onClick={() => onView && onView(params.row.classDocId)}
                >
                  <VisibilityOutlinedIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  sx={{
                    color: themestyle.colors.error,
                    "&:hover": { bgcolor: "rgba(229,57,53,0.12)" },
                  }}
                  onClick={() =>
                    handleDeleteClick(
                      params.row.classDocId,
                      params.row.className,
                      params.row.rawDate,
                    )
                  }
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </>
            )}
          </Box>
        ),
      },
    ];
  }, [
    activeTab,
    isMobile,
    selectedIds,
    canViewPreviousActions,
    onView,
    userRole,
    filteredRows,
  ]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        backgroundColor: isMobile ? "transparent" : "#F7FAF3",
        borderRadius: 3,
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          backgroundColor: themestyle.colors.white,
          borderRadius: "12px",
          border: "1px solid #E5E7EB",
          overflow: "hidden",
        }}
      >
        <AllClassesDataGrid
          rows={filteredRows}
          columns={columns}
          loading={loading}
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          rowCount={activeTab === "current" ? totalCount : filteredRows.length}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[3, 5, 10, 20]}
          getRowId={(row) => row.id}
          onMobileView={(row) => onView && onView(row.classDocId)}
          onMobileEdit={(row) => onEdit && onEdit(row.classDocId)}
          onMobileDelete={(row) =>
            handleDeleteClick(row.classDocId, row.className, row.rawDate)
          }
          onMobileAttendance={(row) => onAttendance && onAttendance(row.classDocId, row.rawDate)}
          instructorValue={selectedInstructor}
          dayValue={selectedDay}
          classTypeValue={selectedClassType}
          onInstructorChange={handleInstructorChange}
          onDayChange={handleDayChange}
          onClassTypeChange={handleClassTypeChange}
          classOptions={classOptions}
          classOptionsLoading={classOptionsLoading}
          instructorOptions={instructorOptions}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          // Bulk selection props
          selectedIds={selectedIds}
          onToggleSelectId={handleToggleSelectId}
          onSelectAll={handleSelectAll}
          onDeselectAll={handleDeselectAll}
          selectAllPages={selectAllPages}
          onBulkDeleteClick={handleBulkDeleteClick}
          totalCount={totalCount}
          canViewPreviousActions={canViewPreviousActions}
        />
      </Box>

      {/* Bulk delete confirm dialog */}
      <ConfirmDialog
        open={bulkDeleteDialogOpen}
        icon={
          <DeleteOutlineIcon
            sx={{ fontSize: 40, color: themestyle.colors.error }}
          />
        }
        message={
          selectionCount > 0
            ? `Are you sure you want to delete ${selectionCount} selected class${selectionCount !== 1 ? "es" : ""}?`
            : ""
        }
        onConfirm={handleBulkDeleteConfirm}
        onClose={() => setBulkDeleteDialogOpen(false)}
        confirmText="Delete"
        confirmButtonColor={themestyle.colors.error}
      />

      <CustomSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        type={snackbarType}
        onClose={() => setSnackbarOpen(false)}
      />
    </Box>
  );
};
export default AllClassesTable;
